import { Column, TablePaginated, TablePaginatedOptions } from '@acceso/design-system';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { RenderActionButtons } from '@/components/RenderActionButtons';
import { StateActiveInTable } from '@/components/StateActiveInTable/StateActiveInTable';
import { TableRow } from '@/components/TableRow';
import { TableRowInfo } from '@/components/TableRowInfo/TableRowInfo';
import { TableRowThumbnail } from '@/components/TableRowThumbnail/TableRowThumbnail';
import { TableUpdatedBy } from '@/components/TableUpdatedBy';
import { useLocationOptions } from '@/features/locations/hooks/useLocationOptions';
import { useUserOptions } from '@/features/users/hooks/useUserOptions';
import { useStateOptions } from '@/hooks/useStateOptions';
import { State } from '@/types';
import { PaginatedTableProps } from '@/types/table';
import { createTableFilterOptions } from '@/utils/table-filters';

import {
  CHANNEL_PROVIDER_TO_TRANSLATION_KEY,
  CHANNEL_TYPE_TO_TRANSLATION_KEY,
} from '../../constants';
import { CHANNEL_MEDIA_TYPE_TO_ICON } from '../../constants/icons';
import { useChannelMediaTypeOptions } from '../../hooks/useChannelMediaTypeOptions';
import { useChannelOptions } from '../../hooks/useChannelOptions';
import { useChannelScopeOptions } from '../../hooks/useChannelScopeOptions';
import { useChannelFilters } from '../../stores/channel-filters';
import { ChannelMediaTypeEnum, ChannelScopeEnum, ChannelType, ChannelTypeEnum } from '../../types';

export function BroadcastersTable({
  items,
  totalItems,
  activeItem,
  onUpdate,
  onDelete,
  onSelect,
  onDuplicate,
  isFetching,
}: PaginatedTableProps<ChannelType>) {
  const { t } = useTranslation();
  const { setFilters, paginationValues, setPage, setRows, setSortBy, setSearchQuery } =
    useChannelFilters();
  const channelMediaTypeOptions = useChannelMediaTypeOptions();
  const parentChannelOptions = useChannelOptions({
    isParentChannel: true,
  });
  const channelScopeOptions = useChannelScopeOptions();
  const { countryOptions, regionOptions, subregionOptions } = useLocationOptions();
  const userOptions = useUserOptions();

  const options: Partial<TablePaginatedOptions> = {
    pagination: {
      changePage: setPage,
      changeRows: setRows,
      currentPage: paginationValues.currentPage,
      itemsPerPage: paginationValues.rowsPerPage,
    },
    setSortBy,
    sortBy: paginationValues.sortBy,
    onSearchChange: setSearchQuery,
    filters: [
      {
        name: 'media_type',
        placeholder: t('Soporte'),
        options: createTableFilterOptions({
          options: channelMediaTypeOptions,
          allOptionLabel: t('Cualquier soporte'),
        }),
        onChange: value => {
          setFilters({
            mediaTypeIn: [value as unknown as ChannelMediaTypeEnum],
          });
        },
      },
      {
        name: 'parentBroadcaster',
        placeholder: t('Emisora madre'),
        options: createTableFilterOptions({
          options: parentChannelOptions,
          allOptionLabel: t('Cualquier emisora madre'),
        }),
        onChange: value => {
          setFilters({
            parentChannelId: value as unknown as string,
          });
        },
      },
      {
        name: 'scope',
        placeholder: t('Ámbito'),
        options: createTableFilterOptions({
          options: channelScopeOptions,
          allOptionLabel: t('Cualquier ámbito'),
        }),
        onChange: value => {
          setFilters({
            scope: value as unknown as ChannelScopeEnum,
          });
        },
      },
      {
        name: 'country',
        placeholder: t('País'),
        options: createTableFilterOptions({
          options: countryOptions,
          allOptionLabel: t('Cualquier país'),
        }),
        onChange: value => {
          setFilters({
            countryCode: value as unknown as string,
          });
        },
      },
      {
        name: 'region',
        placeholder: t('Región'),
        options: createTableFilterOptions({
          options: regionOptions,
          allOptionLabel: t('Cualquier región'),
        }),
        onChange: value => {
          setFilters({
            regionCode: value as unknown as string,
          });
        },
      },
      {
        name: 'subregion',
        placeholder: t('Subregión'),
        options: createTableFilterOptions({
          options: subregionOptions,
          allOptionLabel: t('Cualquier subregión'),
        }),
        onChange: value => {
          setFilters({
            subregionCode: value as unknown as string,
          });
        },
      },
      {
        name: 'updated_by',
        placeholder: t('Actualizado por'),
        options: createTableFilterOptions({
          options: userOptions,
          allOptionLabel: t('Actualizado por cualquiera'),
        }),
        onChange: value => {
          setFilters({
            updatedByIn: [value as unknown as string],
          });
        },
      },
      {
        name: 'state',
        placeholder: t('Estado'),
        options: createTableFilterOptions({
          options: useStateOptions(),
          allOptionLabel: t('Cualquier estado'),
        }),
        onChange: value => {
          setFilters({
            state: value as unknown as State,
          });
        },
      },
    ],
    searchBarPlaceholder: t('Buscar por nombre de emisora o ID'),
  };
  const columns: Column<ChannelType>[] = useMemo(
    () => [
      {
        id: 'broadcaster',
        title: t('Emisora'),
        onColumnClick: item => onSelect?.(item),
        render: item => {
          return <TableRowThumbnail source={item.parameters?.logo_presigned_url} />;
        },
      },
      {
        id: 'name',
        title: t('Emisora'),
        sortable: true,
        onColumnClick: item => onSelect?.(item),
        render: item => {
          const Icon = CHANNEL_MEDIA_TYPE_TO_ICON[item.media_type];
          return (
            <TableRowInfo showTooltip title={item.name} description={item.short_name} icon={Icon} />
          );
        },
      },
      {
        id: 'type',
        title: t('Tipo'),
        onColumnClick: item => onSelect?.(item),
        render: item => {
          return (
            <TableRow
              content={t(
                CHANNEL_TYPE_TO_TRANSLATION_KEY[
                  item.type ?? ChannelTypeEnum.TERRITORIAL_DISCONNECTION
                ]
              )}
            />
          );
        },
      },
      {
        id: 'av_code',
        title: t('ID AV (Legacy)'),
        sortable: true,
        render: item => {
          return <TableRow content={item.av_code || ''} />;
        },
      },
      {
        id: 'provider',
        title: t('Proveedor'),
        sortable: true,
        render: item => {
          return <TableRow content={t(CHANNEL_PROVIDER_TO_TRANSLATION_KEY[item.provider])} />;
        },
      },
      {
        id: 'state',
        title: t('Estado'),
        sortable: true,
        render: item => {
          return <StateActiveInTable status={item.state} />;
        },
      },
      {
        id: 'updated_by',
        title: t('Actualizado por'),
        sortable: true,
        render: item => {
          return <TableUpdatedBy item={item} />;
        },
      },
      {
        id: 'actions',
        title: '',
        className: 'items-end',
        width: '5%',
        render: item => (
          <RenderActionButtons
            item={item}
            onUpdate={() => onUpdate?.(item)}
            onDuplicate={() => onDuplicate?.(item)}
            onDelete={() => onDelete?.(item)}
          />
        ),
      },
    ],
    [t, onSelect, onUpdate, onDelete]
  );

  return (
    <TablePaginated
      options={options}
      columns={columns}
      data={items}
      totalItems={totalItems}
      activeItem={activeItem}
      isFetching={isFetching}
      ignoreEmpty
    />
  );
}
